import Route from '@ember/routing/route';
import { type Registry as Services, service } from '@ember/service';
import { task } from 'ember-concurrency';
import type { DestinationSubscription, User } from 'frontend-utils/models';

export default class DestinationRoute extends Route {
  @service declare access: Services['access'];
  @service declare alerting: Services['alerting'];
  @service declare destination: Services['destination'];
  @service declare errorTracking: Services['error-tracking'];
  @service declare featureToggle: Services['feature-toggle'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];

  @service declare intercom: Services['intercom'];
  @service declare store: Services['store'];

  destinationSubscriptionsTask = task(
    { restartable: true },
    async (id: string) => {
      try {
        const params = {
          typeKey: 'destinationsubscription',
          id,
        };

        // @ts-expect-error: Element implicitly has an 'any' type because expression of type '0' can't be used to index type 'PromiseArray<any, AdapterPopulatedRecordArray<any>>'.
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return await this.store.query<DestinationSubscription>(
          'destination-subscription',
          params,
        )[0];
      } catch (error) {
        this.errorTracking.captureException(error);
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.alerting.showErrorAlert();
      }

      return null;
    },
  );

  async afterModel(
    destinationSubscription: DestinationSubscription,
  ): Promise<void> {
    if (destinationSubscription && destinationSubscription?.id) {
      this.destination.setDestinationSubscription(destinationSubscription);

      await this.initialiseFeatureToggleService(
        this.hotelAndMarketService.user,
        destinationSubscription,
      );

      this.intercom.updateDestination();
    }
  }

  deactivate(): void {
    this.destination.resetDestinationSubscription();
  }

  async initialiseFeatureToggleService(
    user: User,
    destinationSubscription: DestinationSubscription,
  ): Promise<void> {
    // Ember Data id's are strings, convert them to numbers.
    const { id } = destinationSubscription;

    await this.featureToggle.initialise(user, {
      subscriptionId: Number(id),
      isStaffUser: this.access.isStaffUser,
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  async model(params: { id: string }) {
    try {
      const destinationSubscription =
        await this.store.findRecord<DestinationSubscription>(
          'destination-subscription',
          params.id,
        );

      return destinationSubscription;
    } catch (error) {
      this.errorTracking.captureException(error);
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.alerting.showErrorAlert();

      // TODO vfuto: redirect to where?
      return null;
    }
  }
}
